import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Save, 
  Calculator,
  FileText,
  DollarSign
} from 'lucide-react';
import { apiService } from '../services/api';
import { InvoiceDto, CreateCreditMemoDto, CreateCreditMemoItemDto } from '../types';

const CreateCreditMemoPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get('invoiceId');

  const [formData, setFormData] = useState<CreateCreditMemoDto>({
    originalInvoiceId: invoiceId ? parseInt(invoiceId) : 0,
    reason: '',
    notes: '',
    items: [
      {
        description: '',
        quantity: 1,
        rate: 0,
        isTaxable: true,
        unit: '',
        notes: ''
      }
    ]
  });

  const [calculatedTotal, setCalculatedTotal] = useState(0);

  // Fetch invoice details if invoiceId is provided
  const { data: invoice, isLoading: invoiceLoading } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => apiService.getInvoice(parseInt(invoiceId!)),
    enabled: !!invoiceId
  });

  // Calculate total when items change
  useEffect(() => {
    const total = formData.items.reduce((sum, item) => {
      return sum + (item.quantity * item.rate);
    }, 0);
    setCalculatedTotal(total);
  }, [formData.items]);

  // Create credit memo mutation
  const createCreditMemoMutation = useMutation({
    mutationFn: (data: CreateCreditMemoDto) => apiService.createCreditMemo(data),
    onSuccess: (response) => {
      toast.success('Credit memo created successfully');
      queryClient.invalidateQueries({ queryKey: ['credit-memos'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate(`/credit-memos/${response.id}`);
    },
    onError: (error: any) => {
      toast.error('Failed to create credit memo');
      console.error('Error creating credit memo:', error);
    }
  });

  const handleInputChange = (field: keyof CreateCreditMemoDto, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = (index: number, field: keyof CreateCreditMemoItemDto, value: any) => {
    const newItems = [...formData.items];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };

    // Recalculate amount for this item
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = newItems[index].quantity * newItems[index].rate;
    }

    setFormData(prev => ({
      ...prev,
      items: newItems
    }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          description: '',
          quantity: 1,
          rate: 0,
          isTaxable: true,
          unit: '',
          notes: ''
        }
      ]
    }));
  };

  const removeItem = (index: number) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.originalInvoiceId) {
      toast.error('Please select an invoice');
      return;
    }

    if (formData.items.some(item => !item.description.trim())) {
      toast.error('All items must have a description');
      return;
    }

    if (calculatedTotal <= 0) {
      toast.error('Credit memo total must be greater than 0');
      return;
    }

    createCreditMemoMutation.mutate(formData);
  };

  if (invoiceLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/credit-memos')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Create Credit Memo</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Create a new credit memo for an invoice
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Invoice Information */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Invoice Information</h2>
            
            {invoice ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Invoice Number
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white font-medium">
                    {invoice.invoiceNumber}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Client
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {invoice.clientName}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Invoice Total
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white font-medium">
                    ${invoice.total.toFixed(2)}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {invoice.status}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                No invoice selected. Please go back and select an invoice to create a credit memo for.
              </div>
            )}
          </div>

          {/* Credit Memo Details */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Credit Memo Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason for Credit Memo
                </label>
                <input
                  type="text"
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => handleInputChange('reason', e.target.value)}
                  placeholder="e.g., Returned goods, Service not provided"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-accent-color-500 focus:border-accent-color-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes
                </label>
                <input
                  type="text"
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Additional notes"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-accent-color-500 focus:border-accent-color-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Credit Memo Items */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">Credit Memo Items</h2>
              <button
                type="button"
                onClick={addItem}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-accent-color-600 bg-accent-color-100 hover:bg-accent-color-200 dark:bg-accent-color-900 dark:text-accent-color-200 dark:hover:bg-accent-color-800"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Item
              </button>
            </div>

            <div className="space-y-4">
              {formData.items.map((item, index) => (
                <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">Item {index + 1}</h3>
                    {formData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description *
                      </label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        placeholder="Item description"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-accent-color-500 focus:border-accent-color-500 dark:bg-gray-700 dark:text-white"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Quantity
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-accent-color-500 focus:border-accent-color-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Rate
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.rate}
                        onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-accent-color-500 focus:border-accent-color-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Amount
                      </label>
                      <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-white">
                        ${(item.quantity * item.rate).toFixed(2)}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Unit
                      </label>
                      <input
                        type="text"
                        value={item.unit}
                        onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                        placeholder="e.g., each, hour"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-accent-color-500 focus:border-accent-color-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`taxable-${index}`}
                        checked={item.isTaxable}
                        onChange={(e) => handleItemChange(index, 'isTaxable', e.target.checked)}
                        className="h-4 w-4 text-accent-color-600 focus:ring-accent-color-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`taxable-${index}`} className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                        Taxable
                      </label>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Notes
                    </label>
                    <input
                      type="text"
                      value={item.notes}
                      onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                      placeholder="Item notes"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-accent-color-500 focus:border-accent-color-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Total Summary */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Calculator className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-lg font-medium text-gray-900 dark:text-white">Credit Memo Total</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                ${calculatedTotal.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/credit-memos')}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createCreditMemoMutation.isPending || !invoice}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-color-600 hover:bg-accent-color-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-color-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createCreditMemoMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Create Credit Memo
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCreditMemoPage;
